/**
 * Client-side component to initialize Gutenberg anchor replacement
 * This component runs on every page to convert hover-block blocks
 */

"use client";

import { FC, useEffect } from "react";
import {
  initializeAnchorReplacement,
  observeAndReplaceAnchors,
} from "@/lib/helpers/replaceGutenbergAnchors";

interface AnchorReplacementInitializerProps {
  /**
   * Target class names to replace (can be a single string or array of strings)
   */
  targetClassnames?: string | string[];
  /**
   * Fallback href attribute if no child anchor is found (optional)
   */
  fallbackHref?: string;
  /**
   * Whether to enable mutation observer for dynamic content
   */
  enableObserver?: boolean;
  /**
   * Delay in milliseconds before running the replacement
   */
  delay?: number;
}

export const AnchorReplacementInitializer: FC<
  AnchorReplacementInitializerProps
> = ({
  targetClassnames = ["hover-block"],
  fallbackHref,
  enableObserver = true,
  delay = 200,
}) => {
  useEffect(() => {
    let stopObserver: (() => void) | undefined;

    // Initialize anchor replacement
    initializeAnchorReplacement(targetClassnames, fallbackHref, {
      autoRun: true,
      delay,
    });

    // Set up observer for dynamic content if enabled
    if (enableObserver) {
      stopObserver = observeAndReplaceAnchors(targetClassnames, fallbackHref);
    }

    // Cleanup function
    return () => {
      if (stopObserver) {
        stopObserver();
      }
    };
  }, [targetClassnames, fallbackHref, enableObserver, delay]);

  // This component doesn't render anything visible
  return null;
};

export default AnchorReplacementInitializer;
